import { type ReactNode, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { supabase, type CalendarConnectionState, type CalendarProvider } from '@saas-boilerplate/shared';
import { invokeEdgeFn } from '@/lib/edge-functions';
import { timed } from '@/lib/perf';
import { WorkspaceFlowSegmentedStepper } from '@/components/workspace-flow-shell';
import { AppTopBar } from '@/components/app-top-bar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Calendar,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Copy,
  Ear,
  HelpCircle,
  Info,
  Link2,
  Loader2,
  Lock,
  Megaphone,
  Plus,
  Repeat,
  Send,
  Sparkles,
  Upload,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  buildGoogleCalendarAuthUrl,
  buildMicrosoftCalendarAuthUrl,
  storeCalendarOAuthState,
} from '@/lib/calendar-oauth';
import { getCalendarConnectionStateForAddMeeting } from '@/lib/calendar-connection';
import { listCalendarEvents } from '@/lib/calendar-cache';
import { buildCalendarSnippet } from '@/lib/calendar-snippet';
import { cn } from '@/components/ui/utils';

export const MEETING_TYPES = [
  { value: 'standup', label: 'Standup' },
  { value: 'one_on_one', label: '1:1' },
  { value: 'team', label: 'Team' },
  { value: 'retrospective', label: 'Retrospective' },
  { value: 'project_review', label: 'Project review' },
];

const MAX_FUTURE_SERIES_SESSIONS = 100;

/**
 * Immediately schedule the transcript/Recall bot for the soonest upcoming occurrence on enrolment.
 *
 * This is intentionally decoupled from calendar-sync-meeting: that function only schedules a bot
 * *after* a successful Graph/Calendar re-fetch, which can throw for consumer/Teams events and then
 * silently leave the meeting with no bot and no audit trail. Calling the orchestrator directly with
 * the rows we just inserted guarantees the imminent occurrence gets a bot (or a visible failure row)
 * regardless of whether the calendar sync succeeds. The server gates on transcript_bot_enabled,
 * meeting status, a usable URL, and a today/tomorrow window - anything else is a no-op there, and
 * occurrences further out are picked up by the hourly day-ahead scheduler.
 */
async function scheduleEnrolledBotBestEffort(
  meetingRows: Array<{ id: string; starts_at?: string | null; ends_at?: string | null }>,
  workspaceId: string,
): Promise<void> {
  if (!meetingRows?.length) return;
  const nowMs = Date.now();
  // Prefer an occurrence happening right now (started, not yet ended) - that's the one that needs a
  // bot immediately, even if it's close to finishing. Otherwise take the soonest upcoming occurrence.
  // Falls back to the first row so the server-side window/ended checks can decide.
  let inProgress: { id: string } | null = null;
  let soonest: { id: string } | null = null;
  let soonestMs = Number.POSITIVE_INFINITY;
  for (const r of meetingRows) {
    const startMs = r.starts_at ? new Date(r.starts_at).getTime() : Number.NaN;
    if (Number.isNaN(startMs)) continue;
    const endMs = r.ends_at ? new Date(r.ends_at).getTime() : Number.NaN;
    if (startMs <= nowMs && (Number.isNaN(endMs) || endMs > nowMs)) {
      if (!inProgress) inProgress = r;
    } else if (startMs > nowMs && startMs < soonestMs) {
      soonestMs = startMs;
      soonest = r;
    }
  }
  const target = inProgress ?? soonest ?? meetingRows[0];
  if (!target?.id) return;
  try {
    await invokeEdgeFn('transcript-orchestrator', {
      action: 'schedule_meeting',
      meeting_id: target.id,
      workspace_id: workspaceId,
    });
  } catch {
    /* best-effort - the hourly day-ahead scheduler is the backstop */
  }
}

/** Step 3 pills - values must stay within DB check constraint on meeting_series.meeting_type
 *  (migration 0003_phase1_meetings.sql line 102). All five types live here. */
const CONFIGURE_MEETING_CARDS = [
  {
    value: 'standup' as const,
    title: 'Standup',
    description: 'Fast daily check-in focused on progress, blockers, and next steps.',
    icon: CalendarDays,
  },
  {
    value: 'team' as const,
    title: 'Team meeting',
    description: 'Collaborative planning and alignment across priorities and execution.',
    icon: Users,
  },
  {
    value: 'one_on_one' as const,
    title: '1-on-1',
    description: 'Private conversation for coaching, feedback, and growth.',
    icon: UserPlus,
  },
  {
    value: 'retrospective' as const,
    title: 'Retrospective',
    description: 'Look back on the last cycle - what worked, what didn’t, what to change.',
    icon: Repeat,
  },
  {
    value: 'project_review' as const,
    title: 'Project review',
    description: 'Review milestones, risks, and decisions across an active initiative.',
    icon: ClipboardList,
  },
];

/** Instant → `YYYY-MM-DDTHH:mm` using **local** wall time (not UTC via `toISOString()`), for enrol state + `<input type="datetime-local" />`. */
function instantToDatetimeLocalValue(raw: string): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatMeetingDateTimeRange(startsAt: string, endsAt: string): string {
  if (!startsAt) return 'Schedule not set';
  const s = new Date(startsAt);
  if (Number.isNaN(s.getTime())) return 'Schedule not set';
  const dateStr = s.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const startStr = s.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!endsAt) return `${dateStr} • ${startStr}`;
  const e = new Date(endsAt);
  if (Number.isNaN(e.getTime())) return `${dateStr} • ${startStr}`;
  const endStr = e.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${dateStr} • ${startStr} - ${endStr}`;
}

function getEventStartEnd(ev: any, provider: CalendarProvider): {
  start: Date | null;
  end: Date | null;
} {
  if (provider === 'google') {
    const s = ev.start?.dateTime || ev.start?.date;
    const e = ev.end?.dateTime || ev.end?.date;
    const start = s ? new Date(s) : null;
    const end = e ? new Date(e) : null;
    return {
      start: start && !Number.isNaN(start.getTime()) ? start : null,
      end: end && !Number.isNaN(end.getTime()) ? end : null,
    };
  }
  const s = ev.start?.dateTime;
  const e = ev.end?.dateTime;
  const start = s ? new Date(s) : null;
  const end = e ? new Date(e) : null;
  return {
    start: start && !Number.isNaN(start.getTime()) ? start : null,
    end: end && !Number.isNaN(end.getTime()) ? end : null,
  };
}

function formatOccurrenceMeta(start: Date | null, end: Date | null): string {
  if (!start) return 'Next occurrence: time not available';
  const weekday = start.toLocaleDateString(undefined, { weekday: 'long' });
  const time = start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (!end) return `Next occurrence: ${weekday} at ${time}`;
  const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  return `Next occurrence: ${weekday} at ${time}${mins ? ` • ${mins} mins` : ''}`;
}

/** Google expanded instance ids: timed UTC, all-day, or common offset forms. */
function googleRecurringMasterIdFromInstanceId(eventId: string): string | null {
  const id = String(eventId);
  // Timed UTC: <base>_20260417T090000Z
  let m = id.match(/^(.+)_\d{8}T\d{6}Z$/);
  if (m?.[1]) return m[1];
  // Numeric offset: <base>_20260417T090000-0500 or +05:30
  m = id.match(/^(.+)_\d{8}T\d{6}(?:[+-]\d{2}:\d{2}|[+-]\d{4})$/);
  if (m?.[1]) return m[1];
  // All-day instance: <base>_20260417
  m = id.match(/^(.+)_\d{8}$/);
  if (m?.[1]) return m[1];
  return null;
}

function googleEventLooksLikeRecurringInstance(ev: { id?: string }): boolean {
  return googleRecurringMasterIdFromInstanceId(String(ev?.id ?? '')) != null;
}

function googleSplitSeriesBaseId(seriesId: string | null | undefined): string | null {
  const id = String(seriesId || '').trim();
  if (!id) return null;
  const m = id.match(/^(.+)_R\d{8}T\d{6}$/);
  return m?.[1] || null;
}

function getSeriesDedupeKey(ev: any, provider: CalendarProvider): string {
  if (provider === 'google') {
    if (ev.recurringEventId) return String(ev.recurringEventId);
    const id = String(ev.id ?? '');
    const fromInstance = googleRecurringMasterIdFromInstanceId(id);
    if (fromInstance) return fromInstance;
    return id;
  }
  if (ev.seriesMasterId) return String(ev.seriesMasterId);
  if (ev.iCalUId) return String(ev.iCalUId);
  return String(ev.id ?? '');
}

function getEnrolmentCandidateKeysForEvent(ev: any, provider: CalendarProvider): string[] {
  const keys = new Set<string>();
  const add = (raw: string | null | undefined) => {
    const id = String(raw || '').trim();
    if (!id) return;
    keys.add(`${provider}:${id}`);
    if (provider === 'google') {
      const splitBase = googleSplitSeriesBaseId(id);
      if (splitBase) keys.add(`${provider}:${splitBase}`);
    }
  };

  add(getSeriesDedupeKey(ev, provider));
  add(ev?.recurringEventId);
  add(ev?.seriesMasterId);
  add(ev?.id);
  if (provider === 'google') add(googleRecurringMasterIdFromInstanceId(String(ev?.id ?? '')));

  return [...keys];
}

/**
 * Recurring-master id for an enrolment, used for dedupe. Prefers an explicit master id
 * (recurringEventId / seriesMasterId, stored in calendar_series_id); otherwise strips the occurrence
 * suffix off the external_calendar_uid to recover it. This is what lets us recognise that two
 * occurrences of the same recurring event are the same meeting - the raw external_calendar_uid alone
 * differs per occurrence and misses it. (Master ids don't realistically collide across providers, so
 * no provider prefix is needed; the exact-uid check below still distinguishes non-recurring events.)
 */
function seriesMasterId(masterOrSeriesId: string | null | undefined, externalUid: string | null | undefined): string | null {
  const explicit = String(masterOrSeriesId || '').trim();
  if (explicit) return explicit;
  const m = String(externalUid || '').match(/^[a-z]+:(.+)$/i);
  if (!m) return null;
  const id = m[1];
  return googleRecurringMasterIdFromInstanceId(id) ?? googleSplitSeriesBaseId(id) ?? id;
}

/**
 * Find an already-enrolled series in this workspace for the same calendar event OR its recurring
 * master. Matching on external_calendar_uid alone misses duplicates where the same recurring event is
 * enrolled under different occurrences (e.g. the organiser enrolled the series, a guest later enrols a
 * single occurrence) - so we also compare the derived recurring-master id. Returns the existing row
 * (so callers can route into it / auto-transfer ownership) or null.
 */
async function findExistingSeriesForEvent(
  workspaceId: string,
  externalUid: string | null | undefined,
  masterHint: string | null | undefined,
): Promise<{ id: string; owner_user_id: string | null; owner_is_provisional: boolean | null; title: string | null } | null> {
  const { data: rows } = await supabase
    .from('meeting_series')
    .select('id, owner_user_id, owner_is_provisional, title, external_calendar_uid, calendar_series_id')
    .eq('workspace_id', workspaceId);
  if (!rows?.length) return null;
  const want = seriesMasterId(masterHint, externalUid);
  for (const r of rows as Array<any>) {
    if (externalUid && r.external_calendar_uid === externalUid) return r;
    if (want && seriesMasterId(r.calendar_series_id, r.external_calendar_uid) === want) return r;
  }
  return null;
}

function formatAttendeePreviewLine(
  attendeeCount: number | null,
  calendarEventId: string | null,
  manualInviteesRaw?: string,
): string {
  if (attendeeCount != null) {
    return `${attendeeCount} attendee${attendeeCount === 1 ? '' : 's'} invited`;
  }
  if (calendarEventId) {
    return 'Invitee count unavailable';
  }
  // Manual flow: count whatever's been typed into the invitees textarea.
  if (typeof manualInviteesRaw === 'string') {
    const emails = manualInviteesRaw
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
    const n = new Set(emails).size;
    if (n === 0) return 'No invitees yet';
    return `${n} invitee${n === 1 ? '' : 's'}`;
  }
  return 'Invitees not shown for this path';
}

function isRecurringCalendarEvent(ev: any, provider: CalendarProvider): boolean {
  if (provider === 'google') {
    if (Boolean(ev.recurringEventId)) return true;
    if (Array.isArray(ev.recurrence) && ev.recurrence.length > 0) return true;
    return googleEventLooksLikeRecurringInstance(ev);
  }
  if (Boolean(ev.seriesMasterId)) return true;
  return Array.isArray(ev.recurrence) && ev.recurrence.length > 0;
}

type WorkspaceLite = {
  id: string;
  name?: string;
  inbound_key?: string | null;
  /** workspaces.settings - read for default_capture_method when stamping a new series. */
  settings?: Record<string, unknown> | null;
};

const WISDOM_IMAGE =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuByWOJ8yLw1uenaBa530FERCFY0fIDuOMwliiLZdq-J7kcCfhh2rKFKN1-6Il363ciKPP3pB1DObxAz5JBgMTADyaUz2N0rhr25H8hulrBS_Az0TFzSpyIXkSwQnRqx8MpEk5kcl4t3CfRrhO9rLK9r4YIeEz5NQm4hNzoUNvzJkxAF3TBKbLqMy46hdB0I5Y4V_sXvkiKbNJeovDhD7nFpjSeFH3XnkVEiVf9VDP4jm9V_EaEIUOjD_JJ3lN4sJz-tmn2dAWwY-ATo';

function makeVerificationId(): string {
  const c = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const seg = () =>
    Array.from({ length: 3 }, () => c[Math.floor(Math.random() * c.length)]).join('');
  return `USP-${seg()}-${seg()}-${seg()}`;
}

function makeSessionFingerprint(): { vid: string; slug: string } {
  const vid = makeVerificationId();
  const slug = vid.replace(/^USP-/, '').toLowerCase();
  return { vid, slug };
}

/** Same segmented bar as workspace onboarding (`WorkspaceFlowSegmentedStepper`). */
function EnrolmentFlowStepper({ current }: { current: 1 | 2 | 3 }) {
  return (
    <div className="mb-8 flex w-full justify-center px-2 md:mb-10">
      <WorkspaceFlowSegmentedStepper current={current} total={2} className="max-w-[280px]" />
    </div>
  );
}

export function MeetingEnrolWizard({
  workspace,
  userId,
  inboundAddress,
  onCancel,
  onCreated,
  onSupport,
  userProfile,
  onSignOut,
  onSwitchWorkspace,
  onHelp,
}: {
  workspace: WorkspaceLite;
  userId: string;
  inboundAddress: string;
  onCancel: () => void;
  onCreated: (seriesId: string) => void;
  onSupport?: () => void;
  userProfile?: { full_name?: string; email?: string; role?: string } | null;
  onSignOut?: () => void;
  onSwitchWorkspace?: () => void;
  onHelp?: () => void;
}) {
  /** How newly enrolled series are captured, from the workspace default (PRD_DEVICE_AUTO_RECORD
   *  §4.3). The wizard's own toggle governs WHETHER to capture; this governs how. */
  const workspaceDefaultCaptureMethod: 'bot' | 'desktop' =
    (workspace?.settings as { default_capture_method?: unknown } | null)?.default_capture_method === 'desktop'
      ? 'desktop'
      : 'bot';
  const [step, setStep] = useState<'pick' | 'configure'>('pick');
  const [sessionFingerprint, setSessionFingerprint] = useState<{ vid: string; slug: string } | null>(
    null,
  );
  const [title, setTitle] = useState('');
  const [meetingType, setMeetingType] = useState('team');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [isRecurring, setIsRecurring] = useState(true);
  const [externalUid, setExternalUid] = useState<string | null>(null);
  const [enrolmentSource, setEnrolmentSource] = useState<string>('manual');
  const [calendarProvider, setCalendarProvider] = useState<string | null>(null);
  const [calendarEventId, setCalendarEventId] = useState<string | null>(null);
  const [calendarSeriesId, setCalendarSeriesId] = useState<string | null>(null);
  /**
   * Whether the connected calendar's owner is the organizer of the picked event. Drives the
   * "Insert to Calendar" button - non-organizers can PATCH the description but it only updates
   * their private copy on Google/Microsoft, never propagating to other attendees.
   */
  const [eventIsOrganizer, setEventIsOrganizer] = useState<boolean>(true);
  const [attendeeCount, setAttendeeCount] = useState<number | null>(null);
  const [futureInstanceCount, setFutureInstanceCount] = useState<number | null>(null);
  // Multi-select batch enrolment from the calendar picker.
  const [multiSelect, setMultiSelect] = useState(false);
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  const [pasteUrl, setPasteUrl] = useState('');
  const [icsFile, setIcsFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const calendarInsertInFlight = useRef(false);
  const [calProviderPick, setCalProviderPick] = useState<CalendarProvider>('google');
  const [calEvents, setCalEvents] = useState<any[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [enrolledCalendarKeys, setEnrolledCalendarKeys] = useState<Set<string>>(new Set());
  /** Series the current user can take ownership of: they're the calendar organizer but not the current owner. */
  const [claimableSeriesByKey, setClaimableSeriesByKey] = useState<Map<string, string>>(new Map());
  const [claimingSeriesId, setClaimingSeriesId] = useState<string | null>(null);

  const [calendarScenario, setCalendarScenario] = useState<CalendarConnectionState>('NONE');
  const [calendarScenarioProvider, setCalendarScenarioProvider] = useState<CalendarProvider | null>(null);
  const [scenarioLoading, setScenarioLoading] = useState(true);
  const [useManualEntryFlow, setUseManualEntryFlow] = useState(false);
  // Manual-flow invitees: comma/newline-separated emails. Persisted into meeting_attendees on
  // save so recap/nudge dispatchers reach the right people (calendar-sync isn't called here).
  const [manualInvitees, setManualInvitees] = useState('');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setScenarioLoading(true);
      try {
        const res = await getCalendarConnectionStateForAddMeeting({
          workspaceId: workspace.id,
          userId,
        });
        if (cancelled) return;
        setCalendarScenario(res.state);
        setCalendarScenarioProvider(res.provider);
        if (res.provider) setCalProviderPick(res.provider);
      } catch {
        if (cancelled) return;
        setCalendarScenario('NONE');
        setCalendarScenarioProvider(null);
      } finally {
        if (cancelled) return;
        setScenarioLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [workspace.id, userId]);

  useEffect(() => {
    // For CALENDAR_CONNECTED we want the selector list to appear immediately.
    if (step === 'pick' && calendarScenario === 'CALENDAR_CONNECTED' && calendarScenarioProvider) {
      void loadCalendarEvents();
      void loadEnrolledCalendarKeys(calendarScenarioProvider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, calendarScenario, calendarScenarioProvider]);

  const [captureMode, setCaptureMode] = useState<'passive' | 'active'>('passive');
  /**
   * Notification toggles. Aligned with what `series-settings-flyout` edits - i.e. the *actual*
   * post-meeting + pre-meeting plumbing rather than the legacy slack_digest / email_confirmation
   * flags that were stored as inert JSON on meeting_series.permissions. Defaults match the
   * flyout's initial state when a series is freshly created.
   */
  const [preNudgeEnabled, setPreNudgeEnabled] = useState(true);
  const [postMeetingSummariesEnabled, setPostMeetingSummariesEnabled] = useState(true);
  // DR-24: the old standalone "Update Calendar" step is folded into Configure as a default-ON
  // toggle. Left on (and when the user is the event organizer) we auto-insert the Upstand snippet
  // into the calendar event description on finalize; off skips it.
  const [populateCalendar, setPopulateCalendar] = useState(true);
  // Permissions: removed from the wizard. The three flags previously stored here
  // (guest_editing, external_recording, anonymous_feedback) were inert JSON - never read or
  // enforced anywhere. Re-introduce when the underlying behaviour actually exists.
  /** Persisted on meeting_series.transcript_bot_enabled. */
  const [autoInviteTranscriptBot, setAutoInviteTranscriptBot] = useState(true);
  /**
   * Tracks whether the user explicitly toggled the bot OFF while a video link was present.
   * Lets us auto-default the bot to ON whenever a URL is available *without* re-overriding a
   * deliberate "no thanks" from the user. Cleared the moment they toggle back on.
   */
  const [userTurnedBotOff, setUserTurnedBotOff] = useState(false);

  /**
   * The bot can only be enabled if the wizard sees a video link. Previously the OAuth-picker
   * recurring path was exempted on the assumption that later instances might carry their own
   * Meet URLs, but in practice (e.g. LPVH series) those events had no URL anywhere and the bot
   * silently failed to schedule. Stricter rule: require a real link at enrolment.
   */
  const canEnableTranscriptBot = videoUrl.trim().length > 0;

  /**
   * Keep the bot toggle in sync with URL availability:
   *  - No URL → force off (Switch is also `disabled` so this only matters as a state safety net).
   *  - URL present + user hasn't deliberately turned it off → on by default. Restoring a URL after
   *    it was cleared also re-enables.
   *  - URL present + user did deliberately turn it off → leave off until they toggle back on.
   */
  useEffect(() => {
    if (!canEnableTranscriptBot) {
      if (autoInviteTranscriptBot) setAutoInviteTranscriptBot(false);
      return;
    }
    if (!userTurnedBotOff && !autoInviteTranscriptBot) {
      setAutoInviteTranscriptBot(true);
    }
  }, [canEnableTranscriptBot, userTurnedBotOff, autoInviteTranscriptBot]);

  /** Switch handler - records explicit user intent so URL changes don't undo it. */
  const handleBotToggle = (checked: boolean) => {
    setAutoInviteTranscriptBot(checked);
    setUserTurnedBotOff(!checked && canEnableTranscriptBot);
  };

  const goBackToMeetings = () => {
    onCancel();
  };

  // DR-24: the calendar/identity step is merged into Configure, so "continue from add-details"
  // now lands straight on Configure. We still mint the session fingerprint here because the
  // calendar snippet + direct link (and the slug RPC in saveSeries) depend on it.
  const goToCalendar = () => {
    setSessionFingerprint((p) => p ?? makeSessionFingerprint());
    setStep('configure');
  };

  useEffect(() => {
    if (step === 'configure' && !sessionFingerprint) {
      setSessionFingerprint(makeSessionFingerprint());
    }
  }, [step, sessionFingerprint]);

  /** Build the Upstand calendar snippet for the current session fingerprint (null until minted). */
  const computeCalendarSnippet = (): { snippetText: string; directLink: string; signupUrl: string } | null => {
    const slug = sessionFingerprint?.slug;
    if (!slug) return null;
    const appBase =
      import.meta.env.VITE_APP_URL?.replace(/\/$/, '') ||
      (typeof window !== 'undefined' ? window.location.origin : '');
    const directLink = `${appBase}/meet/direct/${encodeURIComponent(slug)}`;
    const signupUrl = `${appBase}/signup`;
    return { snippetText: buildCalendarSnippet(directLink, signupUrl), directLink, signupUrl };
  };

  /** Normalise the free-form meetingType into one of the five DB-enum values for selection state. */
  const configureCardValue: 'standup' | 'team' | 'one_on_one' | 'retrospective' | 'project_review' =
    meetingType === 'standup'
    || meetingType === 'one_on_one'
    || meetingType === 'team'
    || meetingType === 'retrospective'
    || meetingType === 'project_review'
      ? meetingType
      : 'team';

  const loadCalendarEvents = async () => {
    setLoadingEvents(true);
    try {
      // Shared 60s cache (lib/calendar-cache): the dashboard's "available to enrol" lane usually
      // populates this on app load, so the picker opens instantly instead of re-fetching.
      const json = await listCalendarEvents(workspace.id, calProviderPick);
      setCalEvents(json?.events || []);
    } catch (e: any) {
      toast.error(e?.message || 'Could not load calendar');
      setCalEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadEnrolledCalendarKeys = async (provider: CalendarProvider) => {
    try {
      const { data, error } = await timed('enrol:enrolled-keys', () =>
        supabase
          .from('meeting_series')
          .select('id, external_calendar_uid, calendar_provider, calendar_series_id, owner_user_id')
          .eq('workspace_id', workspace.id),
      );
      if (error) throw error;

      const keys = new Set<string>();
      const seriesIdByKey = new Map<string, { seriesId: string; ownerUserId: string | null }>();

      for (const row of data || []) {
        const rowProvider =
          row?.calendar_provider === 'google' || row?.calendar_provider === 'microsoft'
            ? row.calendar_provider
            : null;
        if (!rowProvider || rowProvider !== provider) continue;

        const rowSeriesId = String(row.id || '').trim();
        const rowOwner = (row.owner_user_id as string | null) || null;
        const addKey = (k: string) => {
          keys.add(k);
          if (rowSeriesId && !seriesIdByKey.has(k)) {
            seriesIdByKey.set(k, { seriesId: rowSeriesId, ownerUserId: rowOwner });
          }
        };

        const seriesCalId = String(row.calendar_series_id || '').trim();
        if (seriesCalId) {
          addKey(`${provider}:${seriesCalId}`);
          if (provider === 'google') {
            const splitBase = googleSplitSeriesBaseId(seriesCalId);
            if (splitBase) addKey(`${provider}:${splitBase}`);
          }
        }

        const externalUid = String(row.external_calendar_uid || '').trim();
        const prefix = `${provider}:`;
        if (externalUid.startsWith(prefix)) {
          const externalId = externalUid.slice(prefix.length);
          addKey(`${provider}:${externalId}`);
          if (provider === 'google') {
            const splitBase = googleSplitSeriesBaseId(externalId);
            if (splitBase) addKey(`${provider}:${splitBase}`);
          }
        }
      }
      setEnrolledCalendarKeys(keys);

      // Of the series we found, which ones can THIS user claim? Conditions:
      //   * Current user is not already the owner.
      //   * Current user is flagged is_organizer=true on at least one meeting in the series
      //     (matched by user_id, since calendar sync populates user_id for known members).
      const candidateSeriesIds = [
        ...new Set(
          [...seriesIdByKey.values()]
            .filter((v) => v.ownerUserId && v.ownerUserId !== userId)
            .map((v) => v.seriesId),
        ),
      ];

      const claimable = new Map<string, string>();
      if (candidateSeriesIds.length > 0) {
        const { data: meetingRows } = await supabase
          .from('meetings')
          .select('id, series_id')
          .in('series_id', candidateSeriesIds);
        const meetingIds = (meetingRows || []).map((m: any) => String(m.id)).filter(Boolean);
        const meetingToSeries = new Map<string, string>();
        for (const m of meetingRows || []) meetingToSeries.set(String((m as any).id), String((m as any).series_id));

        if (meetingIds.length > 0) {
          // Match by user_id OR lowercased email - mirrors the server-side RPC
          // (claim_meeting_series_as_organizer, migration 0037). Calendar sync
          // writes attendees with email only and never resolves to a user_id,
          // so an organiser who signed up after enrolment still has user_id=null
          // on their attendee row.
          const callerEmailLower = (userProfile?.email || '').trim().toLowerCase();
          const orFilter = callerEmailLower
            ? `user_id.eq.${userId},email.eq.${callerEmailLower}`
            : `user_id.eq.${userId}`;
          const { data: attRows } = await supabase
            .from('meeting_attendees')
            .select('meeting_id, is_organizer, user_id, email')
            .in('meeting_id', meetingIds)
            .eq('is_organizer', true)
            .or(orFilter);
          const organizerSeriesIds = new Set<string>();
          for (const r of attRows || []) {
            const seriesId = meetingToSeries.get(String((r as any).meeting_id));
            if (seriesId) organizerSeriesIds.add(seriesId);
          }
          for (const [key, value] of seriesIdByKey.entries()) {
            if (organizerSeriesIds.has(value.seriesId)) claimable.set(key, value.seriesId);
          }
        }
      }
      setClaimableSeriesByKey(claimable);
    } catch {
      setEnrolledCalendarKeys(new Set());
      setClaimableSeriesByKey(new Map());
    }
  };

  const startCalendarOAuth = (provider: CalendarProvider) => {
    if (!workspace?.id) return;
    const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    const redirectUri = `${String(appUrl).replace(/\/$/, '')}/integrations`;
    const state = storeCalendarOAuthState(workspace.id, provider);

    if (provider === 'google') {
      const cid = import.meta.env.VITE_GOOGLE_CALENDAR_CLIENT_ID;
      if (!cid) {
        toast.error('Google OAuth client ID is not configured.');
        return;
      }
      window.location.href = buildGoogleCalendarAuthUrl({
        clientId: cid,
        redirectUri,
        state,
      });
      return;
    }

    const cid = import.meta.env.VITE_MICROSOFT_CALENDAR_CLIENT_ID;
    if (!cid) {
      toast.error('Microsoft OAuth client ID is not configured.');
      return;
    }
    window.location.href = buildMicrosoftCalendarAuthUrl({
      clientId: cid,
      redirectUri,
      state,
    });
  };

  const parseEventTime = (ev: any, provider: CalendarProvider) => {
    if (provider === 'google') {
      const s = ev.start?.dateTime || ev.start?.date;
      const e = ev.end?.dateTime || ev.end?.date;
      return {
        start: s ? instantToDatetimeLocalValue(String(s)) : '',
        end: e ? instantToDatetimeLocalValue(String(e)) : '',
      };
    }
    const s = ev.start?.dateTime;
    const e = ev.end?.dateTime;
    return {
      start: s ? instantToDatetimeLocalValue(String(s)) : '',
      end: e ? instantToDatetimeLocalValue(String(e)) : '',
    };
  };

  const loadFutureInstanceCount = async (
    provider: CalendarProvider,
    seriesId: string,
  ): Promise<number | null> => {
    const trimmedSeriesId = seriesId.trim();
    if (!trimmedSeriesId) return null;
    try {
      const horizonEnd = new Date();
      horizonEnd.setFullYear(horizonEnd.getFullYear() + 5);
      const r = await invokeEdgeFn<{ instances?: Array<{ id: string }> }>(
        'calendar-list-series-instances',
        {
          workspace_id: workspace.id,
          provider,
          calendar_series_id: trimmedSeriesId,
          time_min: new Date().toISOString(),
          time_max: horizonEnd.toISOString(),
          max_results: 250,
        },
      );
      if (!Array.isArray(r?.instances)) return null;
      return Math.min(r.instances.length, MAX_FUTURE_SERIES_SESSIONS);
    } catch {
      return null;
    }
  };

  const claimSeriesOwnership = async (seriesId: string) => {
    if (!seriesId || claimingSeriesId) return;
    setClaimingSeriesId(seriesId);
    try {
      const { error } = await supabase.rpc('claim_meeting_series_as_organizer', {
        p_series_id: seriesId,
      });
      if (error) {
        const code = String(error.message || '');
        if (code.includes('NOT_CALENDAR_ORGANIZER')) {
          toast.error('You are not the calendar organizer of this meeting.');
        } else if (code.includes('NOT_WORKSPACE_MEMBER')) {
          toast.error('You are not an active member of this workspace.');
        } else {
          toast.error('Failed to claim ownership', { description: error.message });
        }
        return;
      }
      toast.success('Ownership transferred to you');
      onCreated(seriesId);
    } finally {
      setClaimingSeriesId(null);
    }
  };

  const enrolFromCalendarApi = async (ev: any, trackMode: 'occurrence' | 'series' = 'occurrence') => {
    const provider = calProviderPick;
    const { start, end } = parseEventTime(ev, provider);

    const seriesId =
      provider === 'google'
        ? String(ev.recurringEventId ?? googleRecurringMasterIdFromInstanceId(String(ev.id ?? '')) ?? '')
        : String(ev.seriesMasterId ?? '');

    const trackSeries = trackMode === 'series' && !!seriesId.trim();
    const chosenCalendarSeriesId = trackSeries ? seriesId.trim() : null;

    setBusy(true);
    try {
      let attendeeCt: number | null = Array.isArray(ev.attendees) ? ev.attendees.length : null;
      const seriesHint =
        provider === 'google'
          ? String(ev.recurringEventId ?? '').trim() || null
          : String(ev.seriesMasterId ?? '').trim() || null;
      if (attendeeCt === null || attendeeCt === 0) {
        try {
          const r = await invokeEdgeFn<{ attendee_count?: number | null }>('calendar-get-event-attendees', {
            workspace_id: workspace.id,
            provider,
            calendar_event_id: String(ev.id),
            calendar_series_id: seriesHint,
          });
          if (typeof r?.attendee_count === 'number') attendeeCt = r.attendee_count;
        } catch {
          /* list payload often omits attendees; keep null */
        }
      }

      setTitle(ev.summary || '(No title)');
      setStartsAt(start);
      setEndsAt(end);
      // `video_url` is resolved server-side (native link → location box → body); fall back to the
      // raw native fields for older payloads.
      setVideoUrl(ev.video_url || ev.hangoutLink || ev.onlineMeeting?.joinUrl || '');
      setAttendeeCount(attendeeCt);

      // `is_recurring_series` is "track the full series in Upstand" (not "the event is recurring").
      setIsRecurring(trackSeries);
      setExternalUid(
        provider === 'google'
          ? `google:${trackSeries ? (ev.recurringEventId ?? ev.id) : ev.id}`
          : `microsoft:${trackSeries ? (ev.seriesMasterId ?? ev.id) : ev.id}`,
      );
      setCalendarProvider(provider);
      setCalendarEventId(ev.id);
      setCalendarSeriesId(chosenCalendarSeriesId);
      setEventIsOrganizer(Boolean((ev as { is_organizer?: boolean }).is_organizer));
      if (trackSeries && chosenCalendarSeriesId) {
        const count = await loadFutureInstanceCount(provider, chosenCalendarSeriesId);
        setFutureInstanceCount(count);
      } else {
        setFutureInstanceCount(null);
      }
      setEnrolmentSource('oauth_picker');
      setUseManualEntryFlow(false);
      goToCalendar();
    } finally {
      setBusy(false);
    }
  };

  const toggleSelectedEvent = (id: string) => {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Enrol a single calendar event with the default config (mirrors the wizard defaults: Team type,
   * passive capture, transcript bot on). Recurring events track the full series and fetch their
   * upcoming instances; one-offs get a single occurrence. Used by the multi-select batch flow.
   */
  const enrolSingleEventWithDefaults = async (
    ev: any,
  ): Promise<{ ok: boolean; seriesId?: string; skipped?: boolean; error?: string }> => {
    const provider = calProviderPick;
    const { start, end } = getEventStartEnd(ev, provider);
    const trackSeries = isRecurringCalendarEvent(ev, provider); // product decision: recurring → series
    const seriesCalId =
      provider === 'google'
        ? String(ev.recurringEventId ?? googleRecurringMasterIdFromInstanceId(String(ev.id ?? '')) ?? '')
        : String(ev.seriesMasterId ?? '');
    const calendarSeriesId = trackSeries && seriesCalId.trim() ? seriesCalId.trim() : null;
    const externalUid =
      provider === 'google'
        ? `google:${trackSeries ? (ev.recurringEventId ?? ev.id) : ev.id}`
        : `microsoft:${trackSeries ? (ev.seriesMasterId ?? ev.id) : ev.id}`;
    const videoUrl = ev.video_url || ev.hangoutLink || ev.onlineMeeting?.joinUrl || '';
    const eventIsOrganizer = Boolean(ev.is_organizer);

    // Dedupe - skip if this workspace already has a series for the same calendar event OR its
    // recurring master (a guest enrolling one occurrence of an already-enrolled series must not
    // create a second series - see findExistingSeriesForEvent).
    const existing = await findExistingSeriesForEvent(workspace.id, externalUid, seriesCalId);
    if (existing) return { ok: true, skipped: true, seriesId: existing.id };

    const { data: seriesRow, error: se } = await supabase
      .from('meeting_series')
      .insert({
        workspace_id: workspace.id,
        owner_user_id: userId,
        owner_is_provisional: Boolean(provider) && !eventIsOrganizer,
        title: ev.summary || '(No title)',
        meeting_type: 'team',
        is_recurring_series: trackSeries,
        external_calendar_uid: externalUid,
        calendar_provider: provider,
        calendar_series_id: calendarSeriesId,
        enrolment_source: 'oauth_picker',
        created_by: userId,
        transcript_bot_enabled: true,
        capture_method: workspaceDefaultCaptureMethod,
        check_in_config: {},
        permissions: {
          enrolment: {
            capture_mode: 'passive',
            slack_digest: true,
            email_confirmation: true,
            guest_editing: false,
            external_recording: true,
            anonymous_feedback: true,
          },
        },
      })
      .select('id')
      .single();
    if (se || !seriesRow) return { ok: false, error: se?.message || 'series insert failed' };

    type MeetingInsert = {
      series_id: string;
      workspace_id: string;
      starts_at: string | null;
      ends_at: string | null;
      video_meeting_url: string | null;
      calendar_event_id: string | null;
      status: 'scheduled';
    };
    let meetingInserts: MeetingInsert[] = [];

    if (trackSeries && calendarSeriesId && (provider === 'google' || provider === 'microsoft')) {
      try {
        const horizonEnd = new Date();
        horizonEnd.setFullYear(horizonEnd.getFullYear() + 5);
        const r = await invokeEdgeFn<{
          instances?: Array<{ id: string; starts_at: string | null; ends_at: string | null; video_meeting_url?: string | null }>;
        }>('calendar-list-series-instances', {
          workspace_id: workspace.id,
          provider,
          calendar_series_id: calendarSeriesId,
          time_min: new Date().toISOString(),
          time_max: horizonEnd.toISOString(),
          max_results: 250,
        });
        const inst = r?.instances;
        if (Array.isArray(inst) && inst.length > 0) {
          const seen = new Set<string>();
          meetingInserts = inst
            .filter((row) => row.id && !seen.has(row.id) && (seen.add(row.id), true))
            .map((row) => ({
              series_id: seriesRow.id,
              workspace_id: workspace.id,
              starts_at: row.starts_at,
              ends_at: row.ends_at,
              video_meeting_url: (row.video_meeting_url ?? videoUrl) || null,
              calendar_event_id: row.id,
              status: 'scheduled' as const,
            }))
            .sort((a, b) => (a.starts_at ? new Date(a.starts_at).getTime() : 0) - (b.starts_at ? new Date(b.starts_at).getTime() : 0))
            .slice(0, MAX_FUTURE_SERIES_SESSIONS);
        }
      } catch {
        /* fall back to a single occurrence below */
      }
    }

    if (meetingInserts.length === 0) {
      meetingInserts = [
        {
          series_id: seriesRow.id,
          workspace_id: workspace.id,
          starts_at: start ? start.toISOString() : null,
          ends_at: end ? end.toISOString() : null,
          video_meeting_url: videoUrl || null,
          calendar_event_id: String(ev.id),
          status: 'scheduled',
        },
      ];
    }

    const { data: meetingRows, error: me } = await supabase
      .from('meetings')
      .insert(meetingInserts)
      .select('id, starts_at, ends_at');
    if (me || !meetingRows?.length) {
      await supabase.from('meeting_series').delete().eq('id', seriesRow.id);
      // A 23505 means one or more of these occurrences are already enrolled
      // (uniq_meetings_workspace_calendar_event, 0118) - treat as already-enrolled, not a failure.
      if ((me as { code?: string } | null)?.code === '23505') {
        return { ok: true, skipped: true, seriesId: seriesRow.id };
      }
      return { ok: false, error: me?.message || 'meeting insert failed' };
    }

    try {
      await invokeEdgeFn('calendar-sync-meeting', {
        meeting_id: meetingRows[0].id,
        workspace_id: workspace.id,
        sync_all_in_series: meetingInserts.length > 1,
      });
    } catch {
      /* invitee sync is best-effort */
    }
    track('meeting_enrolled', { source: 'wizard', recurring: Boolean(trackSeries), has_video_url: Boolean(videoUrl) });    // Schedule the bot straight away, independent of the calendar sync above.
    await scheduleEnrolledBotBestEffort(meetingRows, workspace.id);
    return { ok: true, seriesId: seriesRow.id };
  };

  const batchEnrol = async (events: any[]) => {
    if (events.length === 0) return;
    setBatchBusy(true);
    setBatchProgress({ done: 0, total: events.length });
    let okCount = 0;
    let skipCount = 0;
    let failCount = 0;
    for (let i = 0; i < events.length; i++) {
      const r = await enrolSingleEventWithDefaults(events[i]);
      if (r.ok) {
        if (r.skipped) skipCount++;
        else okCount++;
      } else {
        failCount++;
      }
      setBatchProgress({ done: i + 1, total: events.length });
    }
    setBatchBusy(false);
    setBatchProgress(null);
    setSelectedEventIds(new Set());
    setMultiSelect(false);

    if (okCount > 0) {
      toast.success(
        `Enrolled ${okCount} meeting${okCount === 1 ? '' : 's'}` +
          (skipCount ? ` · ${skipCount} already enrolled` : '') +
          (failCount ? ` · ${failCount} failed` : ''),
      );
      onCancel(); // return to the (refreshed) meetings list
    } else if (skipCount > 0 && failCount === 0) {
      toast.info('All selected meetings were already enrolled');
      onCancel();
    } else {
      toast.error(`Could not enrol the selected meetings${failCount ? ` (${failCount} failed)` : ''}`);
    }
  };

  const resolvePaste = async () => {
    if (!pasteUrl.trim()) {
      toast.error('Paste a URL');
      return;
    }
    setBusy(true);
    try {
      // Paste/URL flows don't represent a directly updatable calendar event.
      setCalendarProvider(null);
      setCalendarEventId(null);
      setEventIsOrganizer(true);
      setCalendarSeriesId(null);
      setExternalUid(null);
      setIsRecurring(false);
      setAttendeeCount(null);
      setFutureInstanceCount(null);

      const json = await invokeEdgeFn<{
        fields?: Record<string, unknown>;
        confidence?: string;
        hint?: string;
      }>('resolve-calendar-input', {
        url: pasteUrl.trim(),
        workspace_id: workspace.id,
      });
      const f = json.fields || {};
      if (typeof f.title === 'string') setTitle(f.title);
      if (f.starts_at) setStartsAt(instantToDatetimeLocalValue(String(f.starts_at)));
      if (f.ends_at) setEndsAt(instantToDatetimeLocalValue(String(f.ends_at)));
      if (typeof f.video_meeting_url === 'string') setVideoUrl(f.video_meeting_url);
      if (typeof f.is_recurring_series === 'boolean') setIsRecurring(f.is_recurring_series);
      if (typeof f.external_calendar_uid === 'string') setExternalUid(f.external_calendar_uid);
      setEnrolmentSource('paste_url');
      goToCalendar();
      if (json.confidence === 'partial') {
        toast.message('Partial match', {
          description: json.hint || 'Fill remaining fields manually.',
        });
      }
    } catch (e: any) {
      toast.error(e?.message || 'Could not resolve URL');
    } finally {
      setBusy(false);
    }
  };

  const resolveIcsFile = async () => {
    if (!icsFile) {
      toast.error('Choose an .ics file');
      return;
    }
    setBusy(true);
    try {
      // ICS upload flow doesn't necessarily map to a directly updatable calendar event.
      setCalendarProvider(null);
      setCalendarEventId(null);
      setEventIsOrganizer(true);
      setCalendarSeriesId(null);
      setExternalUid(null);
      setIsRecurring(false);
      setAttendeeCount(null);
      setFutureInstanceCount(null);

      const text = await icsFile.text();
      const json = await invokeEdgeFn<{ fields?: Record<string, unknown> }>('resolve-calendar-input', {
        ics_text: text,
        workspace_id: workspace.id,
      });
      const f = json.fields || {};
      if (typeof f.title === 'string') setTitle(f.title);
      if (f.starts_at) setStartsAt(instantToDatetimeLocalValue(String(f.starts_at)));
      if (f.ends_at) setEndsAt(instantToDatetimeLocalValue(String(f.ends_at)));
      if (typeof f.video_meeting_url === 'string') setVideoUrl(f.video_meeting_url);
      if (typeof f.is_recurring_series === 'boolean') setIsRecurring(f.is_recurring_series);
      if (typeof f.external_calendar_uid === 'string') setExternalUid(f.external_calendar_uid);
      setEnrolmentSource('ics_upload');
      goToCalendar();
    } catch (e: any) {
      toast.error(e?.message || 'Could not read ICS');
    } finally {
      setBusy(false);
    }
  };

  const copyInbound = () => {
    if (!inboundAddress) {
      toast.error('Workspace inbound address is not ready yet.');
      return;
    }
    void navigator.clipboard.writeText(inboundAddress);
    toast.success('Copied');
  };

  const sendInviteInstead = () => {
    if (!inboundAddress) {
      toast.error('Workspace inbound address is not ready yet.');
      return;
    }
    const subject = encodeURIComponent('Meeting invite');
    const body = encodeURIComponent(
      `Add ${inboundAddress} to your calendar invitation, or forward meeting details to this address.`,
    );
    window.location.href = `mailto:${encodeURIComponent(inboundAddress)}?subject=${subject}&body=${body}`;
  };

  const saveSeries = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    // Validate: bot enabled + no video URL means the bot can never join. The Step 3 Switch is
    // already disabled in this state, but keep this as a server-side backstop in case the flag
    // got through (e.g. browser back-button restored state, or programmatic edge case).
    if (autoInviteTranscriptBot && !videoUrl.trim()) {
      toast.error('Add a video meeting link (Google Meet, Zoom, Teams) or disable the transcript bot before saving.');
      return;
    }
    setBusy(true);
    try {
      // Pre-flight: if this workspace already has a series for the same calendar event OR its
      // recurring master, do not create a duplicate - route into the existing one. Matching the
      // recurring master (not just the occurrence uid) is what stops a guest enrolling a single
      // occurrence of an already-enrolled series from spawning a second series.
      if (externalUid) {
        const existing = await findExistingSeriesForEvent(workspace.id, externalUid, calendarSeriesId);
        if (existing) {
          // If the existing owner is provisional and the current user is the calendar organizer,
          // try to auto-transfer; otherwise just route them into the existing series.
          if (eventIsOrganizer && (existing as { owner_is_provisional?: boolean }).owner_is_provisional) {
            try {
              await supabase.rpc('auto_transfer_provisional_series', { p_series_id: (existing as { id: string }).id });
              toast.success('Took ownership of the existing Upstand series');
            } catch (e: unknown) {
              // Non-fatal - we'll still open the existing series.
              console.warn('auto_transfer_provisional_series failed', e);
            }
          } else {
            toast.info('Already on Upstand', {
              description: `"${(existing as { title?: string }).title || 'This meeting'}" was previously enrolled in this workspace - opening it.`,
            });
          }
          onCreated((existing as { id: string }).id);
          return;
        }
      }

      const { data: seriesRow, error: se } = await supabase
        .from('meeting_series')
        .insert({
          workspace_id: workspace.id,
          owner_user_id: userId,
          owner_is_provisional: Boolean(calendarProvider) && !eventIsOrganizer,
          title: title.trim(),
          meeting_type: meetingType,
          is_recurring_series: isRecurring,
          external_calendar_uid: externalUid,
          calendar_provider: calendarProvider,
          calendar_series_id: calendarSeriesId,
          enrolment_source: enrolmentSource,
          created_by: userId,
          transcript_bot_enabled: autoInviteTranscriptBot,
          capture_method: workspaceDefaultCaptureMethod,
          // Maps to the flyout's "Post-meeting summaries" toggle.
          email_notifications_enabled: postMeetingSummariesEnabled,
          check_in_config: {},
          permissions: {
            enrolment: {
              capture_mode: captureMode,
            },
          },
        })
        .select('id')
        .single();
      if (se || !seriesRow) throw se || new Error('Failed to create series');

      // The notifications_snapshot trigger seeds meeting_notification_settings with the default
      // pre_meeting_nudge_minutes (120). If the user disabled pre-meeting nudges in the wizard,
      // flip that to null on the just-created series-level row so the dispatcher skips. Mirrors
      // the same write the series-settings flyout does.
      if (!preNudgeEnabled) {
        const { error: nudgeErr } = await supabase
          .from('meeting_notification_settings')
          .update({ pre_meeting_nudge_minutes: null, updated_at: new Date().toISOString() })
          .eq('series_id', seriesRow.id)
          .is('meeting_id', null);
        if (nudgeErr) {
          // Non-fatal: log and continue. Default is "send nudges" which is the safer state.
          console.warn('pre_meeting_nudge_minutes init failed', nudgeErr);
        }
      }

      type MeetingInsert = {
        series_id: string;
        workspace_id: string;
        starts_at: string | null;
        ends_at: string | null;
        video_meeting_url: string | null;
        calendar_event_id: string | null;
        status: 'scheduled';
      };

      let meetingInserts: MeetingInsert[] = [];
      let seriesInstancesFetchError: string | null = null;

      if (
        isRecurring &&
        calendarSeriesId &&
        (calendarProvider === 'google' || calendarProvider === 'microsoft')
      ) {
        try {
          const horizonEnd = new Date();
          horizonEnd.setFullYear(horizonEnd.getFullYear() + 5);
          const r = await invokeEdgeFn<{
            instances?: Array<{
              id: string;
              starts_at: string | null;
              ends_at: string | null;
              video_meeting_url?: string | null;
            }>;
          }>('calendar-list-series-instances', {
            workspace_id: workspace.id,
            provider: calendarProvider,
            calendar_series_id: calendarSeriesId,
            time_min: new Date().toISOString(),
            time_max: horizonEnd.toISOString(),
            max_results: 250,
          });
          const inst = r?.instances;
          if (Array.isArray(inst) && inst.length > 0) {
            const seen = new Set<string>();
            const rows: MeetingInsert[] = inst
              .filter((row) => {
                if (!row.id || seen.has(row.id)) return false;
                seen.add(row.id);
                return true;
              })
              .map((row) => ({
                series_id: seriesRow.id,
                workspace_id: workspace.id,
                starts_at: row.starts_at,
                ends_at: row.ends_at,
                video_meeting_url: (row.video_meeting_url ?? videoUrl) || null,
                calendar_event_id: row.id,
                status: 'scheduled' as const,
              }))
              .sort((a, b) => {
                const ta = a.starts_at ? new Date(a.starts_at).getTime() : 0;
                const tb = b.starts_at ? new Date(b.starts_at).getTime() : 0;
                return ta - tb;
              })
              .slice(0, MAX_FUTURE_SERIES_SESSIONS);
            if (rows.length > 0) meetingInserts = rows;
          }
        } catch (e: any) {
          seriesInstancesFetchError = e?.message || 'Could not load recurring series instances';
        }
      }

      if (
        isRecurring &&
        calendarSeriesId &&
        (calendarProvider === 'google' || calendarProvider === 'microsoft') &&
        seriesInstancesFetchError
      ) {
        throw new Error(
          `${seriesInstancesFetchError}. Please reconnect your calendar and try enrolment again.`,
        );
      }

      if (meetingInserts.length === 0) {
        meetingInserts = [
          {
            series_id: seriesRow.id,
            workspace_id: workspace.id,
            starts_at: startsAt ? new Date(startsAt).toISOString() : null,
            ends_at: endsAt ? new Date(endsAt).toISOString() : null,
            video_meeting_url: videoUrl || null,
            calendar_event_id: calendarEventId,
            status: 'scheduled',
          },
        ];
      }

      const { data: meetingRows, error: me } = await supabase
        .from('meetings')
        .insert(meetingInserts)
        .select('id, starts_at, ends_at');
      if (me || !meetingRows?.length) {
        await supabase.from('meeting_series').delete().eq('id', seriesRow.id);
        throw me || new Error('Failed to create meeting');
      }

      if (
        meetingInserts.some((m) => m.calendar_event_id) &&
        (calendarProvider === 'google' || calendarProvider === 'microsoft')
      ) {
        try {
          await invokeEdgeFn<{ synced?: number }>('calendar-sync-meeting', {
            meeting_id: meetingRows[0].id,
            workspace_id: workspace.id,
            sync_all_in_series: meetingInserts.length > 1,
          });
        } catch (syncErr: any) {
          toast.message('Meeting saved - invitees could not be pulled from calendar yet', {
            description: syncErr?.message || 'Use Re-sync on the meetings list after checking calendar access.',
          });
        }
      } else if (enrolmentSource === 'manual') {
        // Manual flow: calendar-sync-meeting isn't called, so synthesise meeting_attendees rows
        // from the invitees the user typed in (plus the owner as the organiser). Recap and pre-
        // nudge dispatchers key off this table, so without it manual meetings reach nobody.
        try {
          const emails = manualInvitees
            .split(/[\s,;]+/)
            .map((s) => s.trim().toLowerCase())
            .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
          const uniqueInvitees = [...new Set(emails)];
          const ownerEmail = (userProfile?.email || '').toLowerCase().trim() || null;
          // Resolve known user_ids by email (single round-trip).
          const allEmailsToLookUp = [...new Set([...uniqueInvitees, ...(ownerEmail ? [ownerEmail] : [])])];
          const profilesByEmail = new Map<string, string>();
          if (allEmailsToLookUp.length > 0) {
            const { data: profs } = await supabase.from('profiles').select('id, email').in('email', allEmailsToLookUp);
            for (const p of profs || []) {
              const e = String((p as { email?: string | null }).email || '').toLowerCase().trim();
              if (e) profilesByEmail.set(e, String((p as { id: string }).id));
            }
          }
          const rows: Array<{ meeting_id: string; user_id: string | null; email: string | null; is_organizer: boolean; rsvp: string }> = [];
          for (const meeting of meetingRows) {
            rows.push({
              meeting_id: (meeting as { id: string }).id,
              user_id: userId,
              email: ownerEmail,
              is_organizer: true,
              rsvp: 'accepted',
            });
            for (const email of uniqueInvitees) {
              if (email === ownerEmail) continue;
              rows.push({
                meeting_id: (meeting as { id: string }).id,
                user_id: profilesByEmail.get(email) || null,
                email,
                is_organizer: false,
                // rsvp constraint allows only ('accepted','declined','tentative','none'). 'none' is
                // what briefing-content treats as pending - matches calendar-sync's behaviour for
                // invited-but-not-responded attendees.
                rsvp: 'none',
              });
            }
          }
          if (rows.length > 0) {
            // Surface the supabase error explicitly - insert() returns { error } without throwing
            // on RLS/constraint violations, so the previous swallow-everything try/catch missed
            // the rsvp CHECK violation that wiped the batch.
            const { error: attErr } = await supabase.from('meeting_attendees').insert(rows);
            if (attErr) throw attErr;
          }
        } catch (attErr: any) {
          // Non-fatal - the meeting is saved; the user can edit attendees later in the meeting view.
          toast.message('Meeting saved - could not record all invitees', {
            description: attErr?.message || 'Add them from the meeting page.',
          });
        }
      }

      // Schedule the transcript bot straight away for the soonest occurrence, independent of the
      // calendar sync above (which can fail on consumer/Teams events and silently skip the bot).
      if (autoInviteTranscriptBot) {
        await scheduleEnrolledBotBestEffort(meetingRows, workspace.id);
      }

      if (sessionFingerprint?.slug) {
        try {
          await supabase.rpc('create_meeting_series_slug', {
            p_series_id: seriesRow.id,
            p_slug: sessionFingerprint.slug,
          });
        } catch {
          // Non-fatal - the meeting is still created even if slug registration fails
        }
      }

      // DR-24: auto-insert the Upstand description snippet into the calendar event when the user
      // left the "Add to calendar" toggle on AND insertion is actually possible (they're the
      // organizer of a connected event - Google/Microsoft only propagate the organizer's
      // description edits to attendees). Non-fatal: enrolment succeeds regardless.
      if (populateCalendar && eventIsOrganizer && calendarProvider && calendarEventId) {
        const snippet = computeCalendarSnippet();
        if (snippet) {
          try {
            await invokeEdgeFn('calendar-update-description', {
              workspace_id: workspace.id,
              provider: calendarProvider,
              calendar_event_id: calendarEventId,
              calendar_series_id: calendarSeriesId,
              target_type: calendarSeriesId ? 'series' : 'event',
              description_snippet: snippet.snippetText,
            });
          } catch (e: any) {
            toast.message('Meeting saved - calendar description not updated', {
              description: e?.message || 'You can paste the Upstand snippet into the invite yourself.',
            });
          }
        }
      }

      toast.success(
        meetingInserts.length > 1
          ? `Meeting series enrolled (${meetingInserts.length} upcoming sessions)`
          : 'Meeting enrolled',
      );
      onCreated(seriesRow.id);
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const shell = (opts: { main: ReactNode }) => (
    <div className="relative flex min-h-full flex-1 flex-col bg-background font-sans text-foreground selection:bg-[#ffdfa0] selection:text-[rgb(var(--on-primary-container))]">
      <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary-container/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-tertiary-container/10 blur-[150px]" />
      <AppTopBar
        noBleed
        userProfile={userProfile}
        workspaceName={workspace.name}
        onHelp={onHelp ?? onSupport}
        onSignOut={onSignOut}
        onSwitchWorkspace={onSwitchWorkspace}
      />
      <div className="relative z-10 flex min-w-0 flex-1 flex-col overflow-y-auto">{opts.main}</div>
    </div>
  );

  if (step === 'pick') {
    if (scenarioLoading) {
      return shell({
        main: (
          <div className="flex min-h-[50vh] flex-1 items-center justify-center bg-background">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
          </div>
        ),
      });
    }

    if (calendarScenario === 'CALENDAR_CONNECTED' && calendarScenarioProvider && !useManualEntryFlow) {
      const provider = calendarScenarioProvider;
      const dedupedEvents = (() => {
        const bySeries = new Map<string, any>();
        const attendeeScore = (e: any) => (Array.isArray(e.attendees) ? e.attendees.length : -1);
        const recurringScore = (e: any) => (isRecurringCalendarEvent(e, provider) ? 1 : 0);
        for (const ev of calEvents) {
          const recurringKey = getSeriesDedupeKey(ev, provider);
          const prev = bySeries.get(recurringKey);
          if (!prev) {
            bySeries.set(recurringKey, ev);
            continue;
          }
          const a = attendeeScore(ev);
          const b = attendeeScore(prev);
          const ar = recurringScore(ev);
          const br = recurringScore(prev);
          if (a > b || (a === b && ar > br)) {
            bySeries.set(recurringKey, ev);
          }
        }
        return Array.from(bySeries.values());
      })();
      return shell({
        main: (
          <div className="flex w-full flex-col items-center px-6 py-10 lg:px-12 lg:py-16">
            <EnrolmentFlowStepper current={1} />
            <header className="mb-8 w-full max-w-5xl space-y-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                <span>Step 01</span>
                <span className="h-px w-8 bg-outline-variant/40" />
                <span className="font-normal text-stone-500">Add meeting</span>
              </div>
              <h2 className="font-display text-4xl font-light tracking-tight text-foreground md:text-5xl">
                Pick from your {provider === 'google' ? 'Google' : 'Microsoft'} calendar
              </h2>
              <p className="text-lg font-light leading-relaxed text-muted-foreground">
                Select a meeting from your connected calendar.
              </p>
            </header>

            <section className="w-full max-w-5xl">
              <div className="rounded-2xl border border-outline-variant/10 bg-card p-8 shadow-sm">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h3 className="font-display text-lg font-semibold text-foreground">Upcoming meetings</h3>
                  {dedupedEvents.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setMultiSelect((v) => !v);
                        setSelectedEventIds(new Set());
                      }}
                      className="shrink-0 text-sm font-medium text-primary underline underline-offset-4 hover:opacity-80"
                    >
                      {multiSelect ? 'Cancel selection' : 'Select multiple'}
                    </button>
                  )}
                </div>

                {loadingEvents ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : dedupedEvents.length === 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      No upcoming events in your {provider === 'google' ? 'Google' : 'Microsoft'} calendar
                      for the next two weeks. If you expected to see meetings here, re-check below - or
                      reconnect if Upstand&apos;s calendar access was revoked.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void loadCalendarEvents()}
                        disabled={loadingEvents}
                      >
                        <Loader2 className={cn('mr-1.5 h-3.5 w-3.5', loadingEvents ? 'animate-spin' : 'hidden')} />
                        Re-check calendar
                      </Button>
                      <button
                        type="button"
                        onClick={() => startCalendarOAuth(provider)}
                        className="text-sm font-medium text-muted-foreground underline underline-offset-4 hover:text-primary"
                      >
                        Reconnect {provider === 'google' ? 'Google' : 'Microsoft'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="relative rounded-xl border border-outline-variant/25 bg-muted/15 p-1.5">
                      <div
                        className={cn(
                          'max-h-[380px] space-y-2 overflow-y-scroll overscroll-y-contain rounded-lg px-1 py-1 pr-2',
                          'scroll-py-1 [scrollbar-gutter:stable]',
                          '[scrollbar-width:thin]',
                          '[scrollbar-color:rgb(var(--border)/0.85)_rgb(var(--muted)/0.4))]',
                          '[&::-webkit-scrollbar]:w-2.5',
                          '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-border/90 [&::-webkit-scrollbar-thumb]:bg-clip-padding',
                          '[&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-muted/40',
                        )}
                      >
                        {dedupedEvents.map((ev) => {
                          const recurring = isRecurringCalendarEvent(ev, provider);
                          const candidateKeys = getEnrolmentCandidateKeysForEvent(ev, provider);
                          const alreadyEnrolled = candidateKeys.some((k) => enrolledCalendarKeys.has(k));
                          const claimableSeriesId = (() => {
                            for (const k of candidateKeys) {
                              const sid = claimableSeriesByKey.get(k);
                              if (sid) return sid;
                            }
                            return null;
                          })();
                          const canClaim = alreadyEnrolled && Boolean(claimableSeriesId);
                          const isClaiming = claimableSeriesId !== null && claimingSeriesId === claimableSeriesId;
                          const rowDisabled = alreadyEnrolled && !canClaim;
                          const isSelected = selectedEventIds.has(ev.id);
                          const { start, end } = getEventStartEnd(ev, provider);
                          const meta = formatOccurrenceMeta(start, end);
                          return (
                            <button
                              key={ev.id}
                              type="button"
                              disabled={multiSelect ? alreadyEnrolled : rowDisabled || isClaiming}
                              className={cn(
                                'w-full rounded-xl border bg-card px-4 py-3 text-left transition-colors',
                                multiSelect && isSelected
                                  ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/40'
                                  : 'border-outline-variant/20',
                                (multiSelect ? alreadyEnrolled : rowDisabled)
                                  ? 'cursor-not-allowed opacity-60'
                                  : 'hover:bg-muted/50',
                              )}
                              onClick={() => {
                                if (multiSelect) {
                                  if (alreadyEnrolled) return;
                                  toggleSelectedEvent(ev.id);
                                  return;
                                }
                                if (isClaiming) return;
                                if (canClaim && claimableSeriesId) {
                                  void claimSeriesOwnership(claimableSeriesId);
                                  return;
                                }
                                if (alreadyEnrolled) return;
                                // Product decision: recurring events always track the full series.
                                void enrolFromCalendarApi(ev, recurring ? 'series' : 'occurrence');
                              }}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex min-w-0 items-start gap-3">
                                  {multiSelect && (
                                    <span
                                      className={cn(
                                        'mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                                        isSelected
                                          ? 'border-primary bg-primary text-primary-foreground'
                                          : 'border-outline-variant/50 bg-card',
                                      )}
                                      aria-hidden
                                    >
                                      {isSelected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                                    </span>
                                  )}
                                  <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg border border-border/40 bg-muted/40">
                                    <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                                      {start
                                        ? start.toLocaleDateString(undefined, { month: 'short' })
                                        : '-'}
                                    </span>
                                    <span className="font-display text-xl font-bold leading-tight text-primary">
                                      {start ? start.getDate() : '-'}
                                    </span>
                                  </div>
                                  <div className="min-w-0">
                                    <div className="truncate text-base font-medium text-foreground">
                                      {ev.summary || '(No title)'}
                                    </div>
                                    <div className="mt-1 text-sm text-muted-foreground">{meta}</div>
                                  </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  {canClaim ? (
                                    <span className="rounded-full border border-amber-400/40 bg-amber-100/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-900">
                                      {isClaiming ? 'Claiming…' : 'Claim ownership'}
                                    </span>
                                  ) : alreadyEnrolled ? (
                                    <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-primary">
                                      Enrolled
                                    </span>
                                  ) : null}
                                  <span
                                    className={cn(
                                      'rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide',
                                      recurring
                                        ? 'border-tertiary/25 bg-tertiary/15 text-tertiary'
                                        : 'border-amber-300/40 bg-amber-100/80 text-amber-900',
                                    )}
                                  >
                                    {recurring ? 'Recurring' : 'One-off'}
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {dedupedEvents.length > 4 ? (
                        <div
                          className="pointer-events-none absolute bottom-1.5 left-2 right-4 h-14 rounded-b-md bg-gradient-to-t from-card via-card/85 to-transparent"
                          aria-hidden
                        />
                      ) : null}
                    </div>
                    {dedupedEvents.length > 4 ? (
                      <p className="mt-2 text-center text-xs font-medium text-muted-foreground">
                        Scroll to see more meetings
                      </p>
                    ) : null}
                  </div>
                )}

                {multiSelect && dedupedEvents.length > 0 && (
                  <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
                    <span className="text-sm font-medium text-foreground">
                      {selectedEventIds.size} selected
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={batchBusy}
                        onClick={() => {
                          setMultiSelect(false);
                          setSelectedEventIds(new Set());
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={batchBusy || selectedEventIds.size === 0}
                        onClick={() => void batchEnrol(dedupedEvents.filter((e) => selectedEventIds.has(e.id)))}
                      >
                        {batchBusy
                          ? batchProgress
                            ? `Enrolling ${batchProgress.done}/${batchProgress.total}…`
                            : 'Enrolling…'
                          : `Enrol ${selectedEventIds.size} meeting${selectedEventIds.size === 1 ? '' : 's'}`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 text-sm text-muted-foreground">
                Prefer not to use your calendar?{' '}
                <button
                  type="button"
                  className="font-medium text-primary underline underline-offset-4"
                  onClick={() => {
                    setUseManualEntryFlow(true);
                    setEnrolmentSource('manual');
                    // Manual entries usually don't have a video link to paste in, and the save
                    // gate blocks "bot enabled + no link". Default the bot OFF here so users can
                    // save without a hidden trip-wire; they can switch it on after pasting a link.
                    setAutoInviteTranscriptBot(false);
                    // Clear any prior calendar selection to avoid leaking state into manual enrolment.
                    setCalendarProvider(null);
                    setCalendarEventId(null);
                    setCalendarSeriesId(null);
                    setIsRecurring(false);
                    setExternalUid(null);
                    setAttendeeCount(null);
                    setFutureInstanceCount(null);
                    setStep('pick');
                  }}
                >
                  Add a meeting manually instead.
                </button>
              </div>
            </section>
          </div>
        ),
      });
    }

    if (calendarScenario === 'ACCOUNT_ONLY' && calendarScenarioProvider) {
      const provider = calendarScenarioProvider;
      return shell({
        main: (
          <div className="flex flex-col items-center px-6 py-10 lg:px-12 lg:py-16">
            <EnrolmentFlowStepper current={1} />
            <header className="mb-8 w-full max-w-5xl space-y-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                <span>Step 01</span>
                <span className="h-px w-8 bg-outline-variant/40" />
                <span className="font-normal text-stone-500">Add meeting</span>
              </div>
              <h2 className="font-display text-4xl font-light tracking-tight text-foreground md:text-5xl">
                Connect {provider === 'google' ? 'Google Calendar' : 'Microsoft 365'} to continue
              </h2>
              <p className="text-lg font-light leading-relaxed text-muted-foreground">
                Grant calendar access so Upstand can match meetings and insert the Upstand description snippet.
              </p>
            </header>

            <section className="w-full max-w-5xl">
              <div className="rounded-2xl border border-outline-variant/10 bg-card p-8 shadow-sm">
                <h3 className="mb-2 font-display text-lg font-semibold text-foreground">Calendar access</h3>
                <p className="text-sm text-muted-foreground">
                  You’re signed in with {provider === 'google' ? 'Google' : 'Microsoft'}, but calendar access hasn’t been
                  connected yet.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Button
                    type="button"
                    className="flex-1"
                    onClick={() => startCalendarOAuth(provider)}
                  >
                    Connect {provider === 'google' ? 'Google' : 'Microsoft'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => {
                      setEnrolmentSource('manual');
                      if (!title.trim()) setTitle('New meeting');
                      setCalendarProvider(null);
                      setCalendarEventId(null);
                      setCalendarSeriesId(null);
                      setIsRecurring(false);
                      setExternalUid(null);
                      setAttendeeCount(null);
                      setFutureInstanceCount(null);
                      setStep('configure');
                    }}
                  >
                    Add manually instead
                  </Button>
                </div>
              </div>
            </section>
          </div>
        ),
      });
    }

    return shell({
      main: (
        <div className="flex min-h-full w-full flex-col items-center px-6 py-10 lg:px-12 lg:py-16">
          <EnrolmentFlowStepper current={1} />
          <header className="mb-12 w-full max-w-5xl space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              <span>Step 01</span>
              <span className="h-px w-8 bg-outline-variant/40" />
              <span className="font-normal text-stone-500">Add meeting</span>
            </div>
            <h2 className="font-display text-4xl font-light tracking-tight text-foreground md:text-5xl">
              Add meeting
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground">
              Connect your meeting to Upstand.ai&apos;s workspace. Choose your preferred method to bridge your
              workspace.
            </p>
          </header>

          <section className="grid w-full max-w-5xl grid-cols-1 gap-8 md:grid-cols-2">
            <div
              className={cn(
                'group relative flex cursor-pointer flex-col justify-between rounded-2xl border-2 border-transparent bg-surface-container-low p-8 transition-all duration-500 hover:border-outline-variant/10 hover:bg-surface-container-lowest hover:shadow-xl hover:shadow-tertiary/5',
                calendarScenario === 'CALENDAR_CONNECTED' ? 'order-1' : 'order-2',
              )}
            >
              <div>
                <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-md bg-tertiary-container/20">
                  <Plus className="h-7 w-7 text-tertiary" strokeWidth={1.75} />
                </div>
                <h3 className="mb-3 font-display text-2xl font-medium text-foreground">Manual enrolment</h3>
                <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
                  Skip calendar data and fill meeting details yourself.
                </p>
              </div>
              <div className="space-y-3">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Meeting title"
                  className="border-0 border-b-2 border-outline-variant/30 bg-transparent px-0 py-2 text-sm shadow-none focus-visible:border-primary focus-visible:ring-0"
                />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    type="datetime-local"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                    className="border-0 border-b-2 border-outline-variant/30 bg-transparent px-0 py-2 text-sm shadow-none focus-visible:border-primary focus-visible:ring-0"
                  />
                  <Input
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    className="border-0 border-b-2 border-outline-variant/30 bg-transparent px-0 py-2 text-sm shadow-none focus-visible:border-primary focus-visible:ring-0"
                  />
                </div>
                <Input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="Meeting link (optional)"
                  className="border-0 border-b-2 border-outline-variant/30 bg-transparent px-0 py-2 text-sm shadow-none focus-visible:border-primary focus-visible:ring-0"
                />
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="rounded border"
                  />
                  Recurring meeting
                </label>
                <Textarea
                  value={manualInvitees}
                  onChange={(e) => setManualInvitees(e.target.value)}
                  placeholder="Invitees (optional) - emails, comma- or newline-separated"
                  rows={2}
                  className="resize-y border-0 border-b-2 border-outline-variant/30 bg-transparent px-0 py-2 text-sm shadow-none focus-visible:border-primary focus-visible:ring-0"
                />
                <button
                  type="button"
                  onClick={() => {
                    setEnrolmentSource('manual');
                    if (!title.trim()) setTitle('New meeting');
                    // Manual enrolment has no calendar linkage.
                    setCalendarProvider(null);
                    setCalendarEventId(null);
                    setCalendarSeriesId(null);
                    setExternalUid(null);
                    setAttendeeCount(null);
                    setFutureInstanceCount(null);
                    setSessionFingerprint(null);
                    setStep('configure');
                  }}
                  className="enrol-primary-glow-btn w-full rounded-md py-3 text-base font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-opacity hover:opacity-90"
                >
                  Continue with manual enrolment
                </button>
              </div>
            </div>

            <div
              className={cn(
                'group relative flex cursor-pointer flex-col justify-between overflow-hidden rounded-2xl bg-surface-container-low p-8 transition-all duration-500 hover:bg-surface-container-lowest hover:shadow-xl hover:shadow-primary/5',
                calendarScenario === 'CALENDAR_CONNECTED' ? 'order-2' : 'order-1',
              )}
            >
              <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-primary/5 blur-3xl transition-colors group-hover:bg-primary/10" />
              <div className="relative z-10">
                <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-md bg-[#e4e2de]">
                  <Calendar className="h-7 w-7 text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="mb-3 font-display text-2xl font-medium text-foreground">Pick from my calendar</h3>
                <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
                  {calendarScenario === 'CALENDAR_CONNECTED'
                    ? 'You are connected. Jump back to your calendar event selector anytime.'
                    : 'Google or Microsoft - connect your calendar so you can pick an existing meeting and keep it in sync with Upstand.'}
                </p>
              </div>
              <div className="relative z-10 space-y-3">
                {calendarScenario === 'CALENDAR_CONNECTED' ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="flex h-11 w-full items-center justify-center bg-white text-sm font-normal"
                    disabled={busy}
                    onClick={() => {
                      setUseManualEntryFlow(false);
                      setEnrolmentSource('oauth_picker');
                    }}
                  >
                    Choose from calendar
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex h-11 w-full items-center justify-center bg-white text-sm font-normal"
                      disabled={busy}
                      onClick={() => startCalendarOAuth('google')}
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Continue with Google
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex h-11 w-full items-center justify-center bg-white text-sm font-normal"
                      disabled={busy}
                      onClick={() => startCalendarOAuth('microsoft')}
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23">
                        <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                        <path fill="#f35325" d="M1 1h10v10H1z"/>
                        <path fill="#81bc06" d="M12 1h10v10H12z"/>
                        <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                        <path fill="#ffba08" d="M12 12h10v10H12z"/>
                      </svg>
                      Continue with Microsoft
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="order-3 group relative flex cursor-pointer flex-col justify-between rounded-2xl border-2 border-transparent bg-surface-container-low p-8 transition-all duration-500 hover:border-outline-variant/10 hover:bg-surface-container-lowest hover:shadow-xl hover:shadow-tertiary/5">
              <div>
                <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-md bg-tertiary-container/20">
                  <Send className="h-7 w-7 text-tertiary" strokeWidth={1.75} />
                </div>
                <h3 className="mb-3 font-display text-2xl font-medium text-foreground">Invite via email</h3>
                <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
                  Add{' '}
                  <span className="rounded bg-tertiary-container/10 px-1 font-mono text-tertiary">
                    {inboundAddress || 'your workspace inbound address'}
                  </span>{' '}
                  to your calendar invitation or forward the meeting details to us.
                </p>
              </div>
              <div>
                <div className="mb-4 flex items-center justify-between rounded-lg bg-[#e4e2de]/40 p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tertiary-container/30">
                      <Copy className="h-3.5 w-3.5 text-tertiary" />
                    </div>
                    <span className="truncate text-xs font-medium text-muted-foreground">
                      {inboundAddress || 'Configure inbound email in workspace settings'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={copyInbound}
                    className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-tertiary hover:underline"
                  >
                    Copy email
                  </button>
                </div>
                <button
                  type="button"
                  onClick={sendInviteInstead}
                  className="enrol-primary-glow-btn w-full rounded-md py-3 text-base font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-opacity hover:opacity-90"
                >
                  Send invite instead
                </button>
              </div>
            </div>

            <div className="order-4 group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-surface-container-low p-8 transition-all duration-500 hover:bg-surface-container-lowest hover:shadow-xl hover:shadow-primary/5">
              <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-primary/5 blur-3xl transition-colors group-hover:bg-primary/10" />
              <div className="relative z-10">
                <div className="mb-8 flex h-12 w-12 items-center justify-center rounded-md bg-[#e4e2de]">
                  <Upload className="h-7 w-7 text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="mb-3 font-display text-2xl font-medium text-foreground">Upload .ics</h3>
                <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
                  Import a calendar file to extract times and join links.
                </p>
              </div>
              <div className="relative z-10 space-y-4">
                <Input
                  type="file"
                  accept=".ics,text/calendar"
                  className="cursor-pointer border-0 border-b-2 border-outline-variant/30 bg-transparent py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary"
                  onChange={(e) => setIcsFile(e.target.files?.[0] || null)}
                />
                <button
                  type="button"
                  disabled={busy || !icsFile}
                  onClick={() => void resolveIcsFile()}
                  className="enrol-primary-glow-btn w-full rounded-md py-3 text-base font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Parse and continue'}
                </button>
              </div>
            </div>
          </section>

          <div className="mt-auto flex w-full max-w-5xl flex-col gap-6 rounded-2xl bg-gradient-to-r from-surface-container-low to-[#eae8e4] p-6 pt-10 md:flex-row md:items-center">
            <img
              alt=""
              className="h-20 w-20 shrink-0 rounded-full object-cover shadow-sm"
              src={WISDOM_IMAGE}
            />
            <div>
              <p className="text-sm font-medium text-foreground">Enrolment wisdom</p>
              <p className="mt-1 max-w-lg text-xs leading-relaxed text-muted-foreground">
                Providing a direct link allows Upstand.ai to pre-analyze participants&apos; historical context,
                ensuring your workspace is prepared for your arrival.
              </p>
            </div>
          </div>

          <footer className="mt-16 flex w-full max-w-5xl flex-col gap-6 border-t border-outline-variant/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              onClick={goBackToMeetings}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to meetings
            </button>
            <span className="text-xs font-medium italic text-stone-500">
              Need assistance? Contact our workspace guide.
            </span>
          </footer>
        </div>
      ),
    });
  }

  return (
    <div className="relative flex min-h-full flex-1 flex-col bg-background font-sans text-foreground selection:bg-[#ffdfa0] selection:text-[rgb(var(--on-primary-container))]">
      <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary-container/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[500px] w-[500px] rounded-full bg-tertiary-container/10 blur-[150px]" />
      <AppTopBar
        noBleed
        userProfile={userProfile}
        workspaceName={workspace.name}
        onHelp={onHelp ?? onSupport}
        onSignOut={onSignOut}
        onSwitchWorkspace={onSwitchWorkspace}
      />
      <main className="relative z-10 mx-auto w-full max-w-6xl flex-1 px-6 py-6 lg:px-8 lg:py-8">
        <EnrolmentFlowStepper current={2} />

        <header className="mb-5 space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            <span>Step 02</span>
            <span className="h-px w-8 bg-outline-variant/40" />
            <span className="font-normal text-stone-500">Meeting configuration</span>
          </div>
          <h1 className="font-display text-2xl font-light tracking-tight text-foreground md:text-3xl">
            Configure
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Customize how your workspace observes and supports your upcoming meeting.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-5 lg:col-span-8">
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary">Meeting type</h2>
              {/* Compact horizontal pill row. Description moves to a hover tooltip so the
                   selector takes one line instead of three cards' worth of vertical space. */}
              <div className="flex flex-wrap gap-2">
                {CONFIGURE_MEETING_CARDS.map((card) => {
                  const Icon = card.icon;
                  const selected = configureCardValue === card.value;
                  return (
                    <Tooltip key={card.value}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => setMeetingType(card.value)}
                          className={cn(
                            'inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm transition-colors',
                            selected
                              ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/15'
                              : 'border-outline-variant/50 bg-surface-container-low text-foreground hover:border-outline-variant',
                          )}
                        >
                          <Icon
                            className={cn('h-4 w-4', selected ? 'text-primary' : 'text-muted-foreground')}
                            strokeWidth={1.75}
                          />
                          <span className="font-medium">{card.title}</span>
                          {selected && <CheckCircle2 className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">
                        {card.description}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary">AI Assistant</h2>
              {/* Master on/off (formerly "Auto-invite transcript bot" in the right-column block). */}
              <div className="mb-3 rounded-xl border border-outline-variant/40 bg-card px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-container/90">
                      <Bot className="h-4 w-4 text-[rgb(var(--on-primary-container))]" strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">Invite the transcript bot</p>
                      <p className="text-xs text-muted-foreground">
                        A bot will join each meeting to capture transcript + insights.
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={autoInviteTranscriptBot && canEnableTranscriptBot}
                    onCheckedChange={handleBotToggle}
                    disabled={!canEnableTranscriptBot}
                    aria-label="Invite the transcript bot"
                  />
                </div>
                {!canEnableTranscriptBot && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Add a video link to your meeting to enable the transcript bot.
                  </p>
                )}
              </div>

              {/* Mode selector - pill row mirroring the Meeting type pattern. Hover on each pill
                   for the description. Dimmed + non-interactive when the master switch is off. */}
              <div
                className={cn(
                  'flex flex-wrap gap-2 transition-opacity',
                  !autoInviteTranscriptBot && 'pointer-events-none opacity-50',
                )}
                aria-disabled={!autoInviteTranscriptBot}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setCaptureMode('passive')}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm transition-colors',
                        captureMode === 'passive' && autoInviteTranscriptBot
                          ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/15'
                          : 'border-outline-variant/50 bg-surface-container-low text-foreground hover:border-outline-variant',
                      )}
                    >
                      <Ear
                        className={cn(
                          'h-4 w-4',
                          captureMode === 'passive' && autoInviteTranscriptBot ? 'text-primary' : 'text-muted-foreground',
                        )}
                        strokeWidth={1.75}
                      />
                      <span className="font-medium">Passive Listener</span>
                      {captureMode === 'passive' && autoInviteTranscriptBot && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary" strokeWidth={2.25} />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">
                    Captures transcript and action items without interrupting flow. Subtle summaries provided
                    post-meeting.
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() =>
                        toast.message('Upgrade', {
                          description: 'Contact your workspace admin to enable Active Facilitator.',
                        })
                      }
                      className="inline-flex items-center gap-2 rounded-full border border-outline-variant/50 bg-surface-container-low/70 px-3.5 py-1.5 text-sm text-foreground opacity-80 transition-colors hover:border-outline-variant"
                    >
                      <Megaphone className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                      <span className="font-medium">Active Facilitator</span>
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">
                    AI interjects to manage time, redirect tangents, and prompt for missing feedback. Contact your
                    workspace admin to enable.
                  </TooltipContent>
                </Tooltip>
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary">Notifications</h2>
              <ul className="space-y-3 rounded-xl border border-outline-variant/40 bg-card px-4 py-3 shadow-sm">
                <li className="flex items-center justify-between gap-2 text-sm">
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    Pre-meeting nudges
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="rounded-full text-muted-foreground/70 transition-colors hover:text-foreground"
                          aria-label="About pre-meeting nudges"
                        >
                          <Info className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[280px] text-xs leading-relaxed">
                        Sent by email to every attendee ~2 h before the meeting, and as a Slack DM to attendees with
                        Slack connected. Fine-tune timing and channels in Series settings after the meeting is created.
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <Switch checked={preNudgeEnabled} onCheckedChange={setPreNudgeEnabled} />
                </li>
                <li className="flex items-center justify-between gap-2 text-sm">
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                    Post-meeting summaries
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="rounded-full text-muted-foreground/70 transition-colors hover:text-foreground"
                          aria-label="About post-meeting summaries"
                        >
                          <Info className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[280px] text-xs leading-relaxed">
                        Sent by email to each attendee after the meeting ends, and as a Slack DM to attendees with
                        Slack connected. Channel posts can be configured in Integrations. Fine-tune in Series settings
                        after the meeting is created.
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <Switch checked={postMeetingSummariesEnabled} onCheckedChange={setPostMeetingSummariesEnabled} />
                </li>
              </ul>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-primary">Add to calendar</h2>
              {(() => {
                const canAutoInsert = Boolean(calendarProvider && calendarEventId && eventIsOrganizer);
                const hasCalendarEvent = Boolean(calendarProvider && calendarEventId);
                const snippet = computeCalendarSnippet();
                const copySnippet = () => {
                  if (!snippet) return;
                  void navigator.clipboard.writeText(snippet.snippetText);
                  toast.success('Copied to clipboard');
                };
                return (
                  <div className="rounded-xl border border-outline-variant/40 bg-card px-4 py-3 shadow-sm">
                    {canAutoInsert ? (
                      <>
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <p className="font-medium text-foreground">Insert the Upstand block into the invite</p>
                            <p className="text-xs text-muted-foreground">
                              Added to your calendar event description on finalize so Upstand can match it. Turn off to skip.
                            </p>
                          </div>
                          <Switch
                            checked={populateCalendar}
                            onCheckedChange={setPopulateCalendar}
                            aria-label="Insert the Upstand block into the calendar invite"
                          />
                        </div>
                        <button
                          type="button"
                          className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-primary hover:underline"
                          onClick={copySnippet}
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Copy the snippet instead
                        </button>
                      </>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground">
                          {hasCalendarEvent
                            ? "You're not the organizer of this event, so Upstand can't edit the invite directly - copy the block below and paste it into the description (or ask the organizer to enrol)."
                            : 'Paste this block into your calendar invite so Upstand can match the meeting.'}
                        </p>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                          onClick={copySnippet}
                          disabled={!snippet}
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Copy snippet
                        </button>
                      </div>
                    )}
                    {snippet && (
                      <details className="group mt-3 border-t border-outline-variant/30 pt-3">
                        <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
                          <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
                          Preview the block
                        </summary>
                        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 font-mono text-[11px] leading-relaxed text-muted-foreground">{snippet.snippetText}</pre>
                      </details>
                    )}
                  </div>
                );
              })()}
            </section>

          </div>

          <div className="space-y-6 lg:col-span-4">
            <div className="rounded-lg border border-outline-variant/10 bg-surface-container-low p-8">
              <h3 className="mb-5 font-display text-lg font-semibold text-headline">Calendar preview</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Meeting name
                  </Label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New meeting" />
                </div>
                <div className="flex items-center gap-3 text-sm font-medium text-foreground">
                  <CalendarDays className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
                  <span>{formatMeetingDateTimeRange(startsAt, endsAt)}</span>
                </div>
                {videoUrl?.trim() ? (
                  <div className="flex items-center gap-3 text-sm">
                    <Link2 className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
                    <a
                      href={videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-primary underline decoration-primary-container"
                    >
                      {videoUrl}
                    </a>
                  </div>
                ) : null}
                <div className="flex items-center gap-3 text-sm font-medium">
                  <UserPlus className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
                  {formatAttendeePreviewLine(attendeeCount, calendarEventId, enrolmentSource === 'manual' ? manualInvitees : undefined)}
                </div>
                {calendarSeriesId ? (
                  <div className="flex items-center gap-3 text-sm font-medium">
                    <CalendarDays className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
                    <span>
                      {futureInstanceCount != null
                        ? `${futureInstanceCount} future instance${futureInstanceCount === 1 ? '' : 's'}`
                        : 'Future instances unavailable'}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

          </div>
        </div>

        <footer className="mt-5 flex flex-col items-center justify-between gap-4 border-t border-outline-variant/20 pt-4 md:flex-row">
          <button
            type="button"
            className="flex items-center gap-2 font-medium text-muted-foreground transition-colors hover:text-primary"
            onClick={() => setStep('pick')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to add details
          </button>
          <div className="flex w-full flex-col items-stretch gap-4 md:w-auto md:flex-row md:items-center">
            <Button
              type="button"
              className="enrol-primary-glow-btn w-full rounded-md px-8 py-6 font-display text-lg font-semibold tracking-wide text-primary-foreground shadow-xl shadow-primary/10 transition-transform hover:scale-[1.02] hover:opacity-95 md:w-auto"
              disabled={busy}
              onClick={() => void timed('enrol:save-series', () => saveSeries())}
            >
              {busy ? (
                <span className="flex flex-col items-center gap-2 sm:flex-row sm:gap-3">
                  <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
                  <span className="max-w-[18rem] text-center leading-tight sm:max-w-none sm:text-left">
                    Saving &amp; syncing sessions…
                    <span className="mt-1 block text-xs font-normal opacity-90">
                      Large series can take a short while (calendar + scheduling).
                    </span>
                  </span>
                </span>
              ) : (
                'Finalize enrolment'
              )}
            </Button>
          </div>
        </footer>
      </main>
    </div>
  );
}
